import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, ChevronLeft, ChevronRight, Copy, Link2, User, Calendar, BarChart3, List } from 'lucide-react';
import { databaseService, storageService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';
import SerialManagement from './SerialManagement';

const BLOG_COLLECTION = 'blogs';
const BLOG_BUCKET = 'blog_images';
const BLOG_CATEGORIES_COLLECTION = 'blog_categories';

export default function BlogManagement() {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSerialManagement, setShowSerialManagement] = useState(false);

  const [galleryScrollPosition, setGalleryScrollPosition] = useState(0);
  const { showToast } = useToast();

  useEffect(() => {
    loadBlogs();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const result = await databaseService.listDocuments(BLOG_CATEGORIES_COLLECTION);
      if (result.success) {
        const sorted = result.data.documents.sort((a, b) => a.order - b.order);
        setCategories(sorted);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(BLOG_COLLECTION);
      if (result.success) {
        // Admin sees all blogs with status
        const blogsWithStatus = result.data.documents.map(blog => ({
          ...blog,
          status: blog.published ? 'Published' : 'Draft',
          statusColor: blog.published ? 'bg-green-500' : 'bg-yellow-500'
        }));
        setBlogs(blogsWithStatus);
      }
    } catch (error) {
      showToast('Failed to load blogs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file) => {
    try {
      const timestamp = Date.now().toString(36);
      const randomStr = Math.random().toString(36).substring(2, 8);
      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileId = `${timestamp}_${randomStr}.${fileExt}`;
      
      const result = await storageService.uploadFile(BLOG_BUCKET, fileId, file);
      if (result.success) {
        const fileUrl = storageService.getFileView(BLOG_BUCKET, result.data.$id);
        return fileUrl;
      }
      showToast(result.error || 'Failed to upload image', 'error');
      return null;
    } catch (error) {
      console.error('Upload error:', error);
      showToast('Failed to upload image: ' + error.message, 'error');
      return null;
    }
  };

  const handleEdit = (blog) => {
    // Navigate to rich text editor with blog ID in URL
    window.open(`/admin/rich-text-editor/blog/${blog.$id}`, '_blank');
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    try {
      const result = await databaseService.deleteDocument(BLOG_COLLECTION, blogId);
      if (result.success) {
        showToast('Blog deleted successfully', 'success');
        loadBlogs();
      } else {
        showToast('Failed to delete blog', 'error');
      }
    } catch (error) {
      showToast('Error deleting blog', 'error');
    }
  };

  // Handle Add Blog - Always use new editor
  const handleAddBlogSelection = () => {
    // Navigate to rich text editor for new blog
    window.open('/admin/rich-text-editor/blog/new', '_blank');
  };

  // Show Serial Management view
  if (showSerialManagement) {
    return <SerialManagement onBack={() => setShowSerialManagement(false)} />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#4C1D95' }}>
          Blog Management
        </h1>
        
        {/* Serial Management and Add Blog Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => setShowSerialManagement(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <List className="w-5 h-5" />
            Serial Management
          </button>
          
          <button
            onClick={() => handleAddBlogSelection()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105"
            style={{ backgroundColor: '#4C1D95' }}
          >
            <Plus className="w-5 h-5" />
            Add Blog
          </button>
        </div>
      </div>

      {/* Blogs List */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold mb-4" style={{ color: '#4C1D95' }}>
          All Blogs ({blogs.length})
        </h2>
        
        {loading ? (
          <p>Loading blogs...</p>
        ) : blogs.length === 0 ? (
          <p className="text-gray-500">No blogs yet. Add your first blog!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {blogs.map((blog) => (
              <div
                key={blog.$id}
                className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                <img
                  src={blog.thumbnailUrl}
                  alt={blog.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-xs font-bold px-2 py-1 rounded-full text-white"
                      style={{ backgroundColor: '#4C1D95' }}
                    >
                      {blog.category}
                    </span>
                    <div className="flex items-center gap-2">
                      {blog.featured && (
                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-yellow-400 text-yellow-900">
                          Featured
                        </span>
                      )}
                      <span className={`text-xs font-bold px-2 py-1 rounded-full text-white ${blog.statusColor || 'bg-gray-500'}`}>
                        {blog.status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-bold mb-2 line-clamp-2">{blog.title}</h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {blog.description}
                  </p>
                  <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>{blog.publishDate}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(blog)}
                      className="flex-1 flex items-center justify-center px-3 py-2 rounded-lg text-white text-sm transition-all duration-300 hover:scale-105"
                      style={{ backgroundColor: '#4C1D95' }}
                      title="Edit Blog"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <Link
                      to={`/admin/analytics/blogs/${blog.$id}`}
                      className="flex-1 flex items-center justify-center px-3 py-2 rounded-lg bg-purple-500 text-white text-sm transition-all duration-300 hover:scale-105"
                      title="View Analytics"
                    >
                      <BarChart3 className="w-5 h-5" />
                    </Link>
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}${blog.useProjectPrefix ? '/blog/' : '/'}${blog.customSlug || ''}`;
                        if (blog.customSlug) {
                          navigator.clipboard.writeText(url);
                          showToast('Blog link copied!', 'success');
                        } else {
                          showToast('No custom link set for this blog', 'error');
                        }
                      }}
                      className="flex-1 flex items-center justify-center px-3 py-2 rounded-lg bg-blue-500 text-white text-sm transition-all duration-300 hover:scale-105"
                      title="Copy Blog Link"
                    >
                      <Link2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(blog.$id)}
                      className="flex-1 flex items-center justify-center px-3 py-2 rounded-lg bg-red-500 text-white text-sm transition-all duration-300 hover:scale-105"
                      title="Delete Blog"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Cropper Modal */}

    </div>
  );
}

